/*
 * /marine-coordinates — координаты и путевые точки.
 *
 * Отличие от сухопутных конвертеров в трёх вещах, и все три не косметические:
 *  1. основной формат — ГРАДУСЫ И МИНУТЫ С ДОЛЯМИ. Именно он на плоттере, в
 *     приёмнике и в бланке ЦИВ; градусы-минуты-секунды в море не пишут;
 *  2. расстояние и направление считаются ДВУМЯ способами — по локсодромии
 *     (постоянный курс, которым правят) и по ортодромии (кратчайший путь).
 *     На переходе это разные числа, и сухопутный «пеленг» их не различает;
 *  3. позицию нужно уметь ПРОДИКТОВАТЬ по радио, поэтому есть фонетическая
 *     развёртка по алфавиту МСС.
 */

import {
    NM, parseCoords, toDDM, toDMS, greatCircleM, initialBearing,
    rhumbM, rhumbBearing, bearingToPoints, destination,
} from '../maritime/geodesy.js';
import { toUTM, toMGRS, fromMGRS } from '../maritime/utm.js';
import { makeT } from '../maritime/i18n.js';

const B = window.__MC || { T: {}, cfg: {} };
const T = B.T;
const t = makeT(T);
const $ = id => document.getElementById(id);
const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };

const PHONETIC = {
    A: 'Alfa', B: 'Bravo', C: 'Charlie', D: 'Delta', E: 'Echo', F: 'Foxtrot', G: 'Golf',
    H: 'Hotel', I: 'India', J: 'Juliett', K: 'Kilo', L: 'Lima', M: 'Mike', N: 'November',
    O: 'Oscar', P: 'Papa', Q: 'Quebec', R: 'Romeo', S: 'Sierra', T: 'Tango', U: 'Uniform',
    V: 'Victor', W: 'Whiskey', X: 'X-ray', Y: 'Yankee', Z: 'Zulu',
    0: 'Nadazero', 1: 'Unaone', 2: 'Bissotwo', 3: 'Terrathree', 4: 'Kartefour',
    5: 'Pantafive', 6: 'Soxisix', 7: 'Setteseven', 8: 'Oktoeight', 9: 'Novenine',
    '.': 'Decimal', '°': '', "'": '', ' ': '  ',
};

const S = {
    pos: { lat: 55.7558, lon: 37.6173 },
    waypoints: [],
    map: null, marker: null, line: null,
    decimals: 3,
};

/* ── разбор ввода ────────────────────────────────────────────────────────── */
function readInput(raw) {
    const s = String(raw || '').trim();
    if (!s) return null;
    // MGRS проверяется первым: «37U CB 13130 80174» разобралось бы и как пара
    // чисел, и ответ был бы совсем не тот.
    const m = fromMGRS(s);
    if (m) return { ...m, from: 'mgrs' };
    const p = parseCoords(s);
    return p ? { ...p, from: 'geo' } : null;
}

/* ── вывод ───────────────────────────────────────────────────────────────── */
function render() {
    const { lat, lon } = S.pos;
    const ddm = toDDM(lat, lon, S.decimals);
    const dms = toDMS(lat, lon, 1);
    const utm = toUTM(lat, lon);
    const mgrs = toMGRS(lat, lon, 5);

    const box = $('mcOut');
    box.textContent = '';
    const card = (label, value, note, primary) => {
        const c = el('div', 'mc-card' + (primary ? ' primary' : ''));
        c.append(el('div', 'mc-card-l', label));
        const v = el('div', 'mc-card-v', value);
        c.append(v);
        if (note) c.append(el('div', 'mc-card-n', note));
        const b = el('button', 'mc-copy');
        b.type = 'button';
        b.textContent = t('copy');
        b.addEventListener('click', async () => {
            try {
                await navigator.clipboard.writeText(value);
                b.textContent = t('copied');
                setTimeout(() => { b.textContent = t('copy'); }, 1400);
            } catch { window.__toast?.(t('copy_failed'), 'warning'); }
        });
        c.append(b);
        box.append(c);
    };

    card(t('fmt_ddm'), `${ddm.lat} ${ddm.lon}`, t('fmt_ddm_note'), true);
    card(t('fmt_dms'), `${dms.lat} ${dms.lon}`, t('fmt_dms_note'));
    card(t('fmt_dd'), `${lat.toFixed(6)}, ${lon.toFixed(6)}`, t('fmt_dd_note'));
    // Отсечение дробной части, а не округление: координата сетки НАЗЫВАЕТ
    // квадрат, внутри которого стоит точка, и MGRS определён именно так.
    // При округлении UTM и MGRS расходились на метр в последней цифре —
    // одна и та же точка выглядела двумя.
    if (utm) card(t('fmt_utm'), `${utm.zone}${utm.band} ${Math.floor(utm.easting)} ${Math.floor(utm.northing)}`, t('fmt_utm_note'));
    if (mgrs) card(t('fmt_mgrs'), mgrs.text, t('fmt_mgrs_note'));

    $('mcPhonetic').textContent = phonetic(`${ddm.lat} ${ddm.lon}`);
    updateMap();
    renderLegs();
}

function phonetic(s) {
    return [...s.toUpperCase()]
        .map(ch => PHONETIC[ch] !== undefined ? PHONETIC[ch] : ch)
        .filter(x => x !== '')
        .join(' ')
        .replace(/\s{2,}/g, '   ');
}

/* ── карта ───────────────────────────────────────────────────────────────── */
function initMap() {
    if (!window.L || S.map) return;
    S.map = L.map('mcMap', { worldCopyJump: true }).setView([S.pos.lat, S.pos.lon], 6);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18, attribution: '© OpenStreetMap',
    }).addTo(S.map);
    S.marker = L.marker([S.pos.lat, S.pos.lon], { draggable: true }).addTo(S.map);
    S.marker.on('dragend', () => {
        const ll = S.marker.getLatLng();
        S.pos = { lat: ll.lat, lon: ((ll.lng + 540) % 360) - 180 };
        $('mcInput').value = `${S.pos.lat.toFixed(6)} ${S.pos.lon.toFixed(6)}`;
        render();
    });
    S.map.on('click', e => {
        S.pos = { lat: e.latlng.lat, lon: ((e.latlng.lng + 540) % 360) - 180 };
        $('mcInput').value = `${S.pos.lat.toFixed(6)} ${S.pos.lon.toFixed(6)}`;
        render();
    });
}

function updateMap() {
    if (!S.map) return;
    S.marker.setLatLng([S.pos.lat, S.pos.lon]);
    // Карта следует за позицией, но только когда та ушла ЗА КАДР: иначе
    // всякий ввод дёргал бы карту у человека, который её разглядывает.
    // Без этого вставленная позиция Нью-Йорка оставляла карту на Москве.
    if (!S.map.getBounds().pad(-0.12).contains([S.pos.lat, S.pos.lon])) {
        S.map.setView([S.pos.lat, S.pos.lon], S.map.getZoom(), { animate: false });
    }
    if (S.line) { S.map.removeLayer(S.line); S.line = null; }
    if (S.waypoints.length >= 2) {
        // Ортодромия рисуется по точкам: прямая на плоской карте — это
        // локсодромия, и показывать её как «кратчайший путь» было бы враньём.
        const pts = [];
        for (let i = 0; i < S.waypoints.length - 1; i++) {
            const a = S.waypoints[i], b = S.waypoints[i + 1];
            for (let k = 0; k <= 40; k++) {
                const f = k / 40;
                const d = greatCircleM(a, b) / 6371008.8;
                if (d < 1e-9) { pts.push([a.lat, a.lon]); continue; }
                const A1 = Math.sin((1 - f) * d) / Math.sin(d), B1 = Math.sin(f * d) / Math.sin(d);
                const φ1 = a.lat * Math.PI / 180, λ1 = a.lon * Math.PI / 180;
                const φ2 = b.lat * Math.PI / 180, λ2 = b.lon * Math.PI / 180;
                const x = A1 * Math.cos(φ1) * Math.cos(λ1) + B1 * Math.cos(φ2) * Math.cos(λ2);
                const y = A1 * Math.cos(φ1) * Math.sin(λ1) + B1 * Math.cos(φ2) * Math.sin(λ2);
                const z = A1 * Math.sin(φ1) + B1 * Math.sin(φ2);
                pts.push([Math.atan2(z, Math.hypot(x, y)) * 180 / Math.PI, Math.atan2(y, x) * 180 / Math.PI]);
            }
        }
        S.line = L.polyline(pts, { color: '#6366f1', weight: 3 }).addTo(S.map);
    }
}

/* ── путевые точки ───────────────────────────────────────────────────────── */
function addWaypoint() {
    const name = ($('mcName').value || '').trim() || `WP${S.waypoints.length + 1}`;
    S.waypoints.push({ name, lat: S.pos.lat, lon: S.pos.lon });
    $('mcName').value = '';
    save();
    renderWaypoints();
}

function renderWaypoints() {
    const list = $('mcWpList');
    list.textContent = '';
    if (!S.waypoints.length) {
        list.append(el('p', 'mc-note', t('wp_empty')));
        $('mcLegs').textContent = '';
        updateMap();
        return;
    }
    S.waypoints.forEach((w, i) => {
        const row = el('div', 'mc-wp');
        const d = toDDM(w.lat, w.lon, S.decimals);
        row.append(el('span', 'mc-wp-i', String(i + 1)));
        const nm = el('input', 'mc-wp-name');
        nm.value = w.name;
        nm.addEventListener('input', () => { w.name = nm.value; save(); renderLegs(); });
        row.append(nm);
        const go = el('button', 'mc-wp-pos');
        go.type = 'button';
        go.textContent = `${d.lat} ${d.lon}`;
        go.addEventListener('click', () => { S.pos = { lat: w.lat, lon: w.lon }; $('mcInput').value = `${w.lat.toFixed(6)} ${w.lon.toFixed(6)}`; render(); S.map?.panTo([w.lat, w.lon]); });
        row.append(go);
        const up = el('button', 'mc-wp-btn', '↑');
        up.type = 'button'; up.disabled = i === 0;
        up.addEventListener('click', () => { [S.waypoints[i - 1], S.waypoints[i]] = [S.waypoints[i], S.waypoints[i - 1]]; save(); renderWaypoints(); });
        const dn = el('button', 'mc-wp-btn', '↓');
        dn.type = 'button'; dn.disabled = i === S.waypoints.length - 1;
        dn.addEventListener('click', () => { [S.waypoints[i + 1], S.waypoints[i]] = [S.waypoints[i], S.waypoints[i + 1]]; save(); renderWaypoints(); });
        const rm = el('button', 'mc-wp-btn danger', '×');
        rm.type = 'button';
        rm.setAttribute('aria-label', t('wp_delete'));
        rm.addEventListener('click', () => { S.waypoints.splice(i, 1); save(); renderWaypoints(); });
        row.append(up, dn, rm);
        list.append(row);
    });
    renderLegs();
    updateMap();
}

function renderLegs() {
    const box = $('mcLegs');
    box.textContent = '';
    if (S.waypoints.length < 2) return;

    const tbl = el('table', 'mc-table');
    const head = el('tr');
    for (const k of ['leg_from', 'leg_rhumb_d', 'leg_rhumb_b', 'leg_gc_d', 'leg_gc_b', 'leg_diff']) head.append(el('th', null, t(k)));
    tbl.append(head);

    let sumR = 0, sumG = 0;
    for (let i = 0; i < S.waypoints.length - 1; i++) {
        const a = S.waypoints[i], b = S.waypoints[i + 1];
        const rd = rhumbM(a, b) / NM, gd = greatCircleM(a, b) / NM;
        const rb = rhumbBearing(a, b), gb = initialBearing(a, b);
        sumR += rd; sumG += gd;
        const tr = el('tr');
        tr.append(el('td', null, `${a.name} → ${b.name}`));
        tr.append(el('td', 'num', rd.toFixed(1)));
        tr.append(el('td', 'num', rb.toFixed(1) + '° ' + bearingToPoints(rb)));
        tr.append(el('td', 'num', gd.toFixed(1)));
        tr.append(el('td', 'num', gb.toFixed(1) + '°'));
        tr.append(el('td', 'num', (rd - gd).toFixed(1)));
        tbl.append(tr);
    }
    const foot = el('tr', 'total');
    foot.append(el('td', null, t('leg_total')));
    foot.append(el('td', 'num', sumR.toFixed(1)));
    foot.append(el('td', null, ''));
    foot.append(el('td', 'num', sumG.toFixed(1)));
    foot.append(el('td', null, ''));
    foot.append(el('td', 'num', (sumR - sumG).toFixed(1)));
    tbl.append(foot);
    box.append(tbl);
    box.append(el('p', 'mc-note', t('legs_note')));
}

/* ── GPX ─────────────────────────────────────────────────────────────────── */
function buildGpx() {
    const esc = s => String(s).replace(/[<>&"']/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' }[c]));
    const pts = S.waypoints.map(w =>
        `  <wpt lat="${w.lat.toFixed(7)}" lon="${w.lon.toFixed(7)}"><name>${esc(w.name)}</name></wpt>`).join('\n');
    const rte = S.waypoints.length >= 2
        ? `  <rte><name>${esc($('mcRouteName').value || 'Route')}</name>\n`
          + S.waypoints.map(w => `    <rtept lat="${w.lat.toFixed(7)}" lon="${w.lon.toFixed(7)}"><name>${esc(w.name)}</name></rtept>`).join('\n')
          + '\n  </rte>'
        : '';
    return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Timbrica" xmlns="http://www.topografix.com/GPX/1/1">
${pts}
${rte}
</gpx>
`;
}

function importGpx(text) {
    try {
        const doc = new DOMParser().parseFromString(text, 'application/xml');
        if (doc.querySelector('parsererror')) throw new Error('xml');
        const nodes = [...doc.querySelectorAll('rtept'), ...doc.querySelectorAll('wpt')];
        const seen = new Set();
        const out = [];
        for (const n of nodes) {
            const lat = parseFloat(n.getAttribute('lat')), lon = parseFloat(n.getAttribute('lon'));
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
            const key = lat.toFixed(6) + ',' + lon.toFixed(6);
            if (seen.has(key)) continue;
            seen.add(key);
            out.push({ name: n.querySelector('name')?.textContent?.trim() || `WP${out.length + 1}`, lat, lon });
        }
        if (!out.length) { window.__toast?.(t('gpx_empty'), 'warning'); return; }
        S.waypoints = out;
        save();
        renderWaypoints();
        if (S.map && out.length) S.map.fitBounds(out.map(w => [w.lat, w.lon]), { padding: [30, 30] });
        window.__toast?.(t('gpx_loaded', { n: out.length }), 'success');
    } catch {
        window.__toast?.(t('gpx_bad'), 'error');
    }
}

/* ── хранение ────────────────────────────────────────────────────────────── */
function save() {
    try { window.__toolSettings?.save('marine-coordinates', 'wp', S.waypoints); } catch { /* приватный режим */ }
}
function restore() {
    try {
        const w = window.__toolSettings?.load('marine-coordinates', 'wp', []);
        if (Array.isArray(w)) S.waypoints = w.filter(x => Number.isFinite(x?.lat) && Number.isFinite(x?.lon));
    } catch { /* приватный режим */ }
}

/* ── подключение ─────────────────────────────────────────────────────────── */
function bind() {
    const inp = $('mcInput');
    let deb;
    const apply = () => {
        const r = readInput(inp.value);
        $('mcErr').hidden = !!r || !inp.value.trim();
        if (r) { S.pos = { lat: r.lat, lon: r.lon }; render(); }
    };
    inp.addEventListener('input', () => { clearTimeout(deb); deb = setTimeout(apply, 200); });

    $('mcHere').addEventListener('click', () => {
        if (!navigator.geolocation) { window.__toast?.(t('geo_unavailable'), 'warning'); return; }
        navigator.geolocation.getCurrentPosition(
            p => {
                inp.value = `${p.coords.latitude.toFixed(6)} ${p.coords.longitude.toFixed(6)}`;
                apply();
                S.map?.setView([p.coords.latitude, p.coords.longitude], 11);
            },
            () => window.__toast?.(t('geo_denied'), 'warning'),
            { enableHighAccuracy: true, timeout: 10000 });
    });

    $('mcDecimals').addEventListener('change', e => { S.decimals = Number(e.target.value); render(); renderWaypoints(); });
    $('mcAdd').addEventListener('click', addWaypoint);
    $('mcName').addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); addWaypoint(); } });
    $('mcClear').addEventListener('click', () => { S.waypoints = []; save(); renderWaypoints(); });

    $('mcExport').addEventListener('click', () => {
        if (!S.waypoints.length) { window.__toast?.(t('wp_empty'), 'warning'); return; }
        const name = (($('mcRouteName').value || 'route').trim().replace(/[^\w\s.-]+/g, '_') || 'route') + '.gpx';
        const blob = new Blob([buildGpx()], { type: 'application/gpx+xml' });
        window.__downloadFile ? window.__downloadFile(blob, name) : (() => {
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob); a.download = name; a.click();
        })();
        window.__aeFunnel?.('gpx_export', { n: S.waypoints.length });
    });
    $('mcImport').addEventListener('change', e => {
        const f = e.target.files?.[0];
        if (!f) return;
        f.text().then(importGpx).catch(() => window.__toast?.(t('gpx_bad'), 'error'));
        e.target.value = '';
    });
    $('mcPhoneticCopy').addEventListener('click', async () => {
        try { await navigator.clipboard.writeText($('mcPhonetic').textContent); window.__toast?.(t('copied'), 'success'); }
        catch { window.__toast?.(t('copy_failed'), 'warning'); }
    });
}

function boot() {
    try {
        bind();
        restore();
        $('mcInput').value = `${S.pos.lat.toFixed(6)} ${S.pos.lon.toFixed(6)}`;
        initMap();
        render();
        renderWaypoints();
        window.__stage?.('ready');
    } catch (err) {
        window.__handleError?.(err, 'marine-coordinates');
        const b = $('mcOut');
        if (b) b.textContent = t('load_error');
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
